import React, { useEffect, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { Plus, Trash2, Save, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
  volume: "", issue: "", year: String(new Date().getFullYear()),
  publication_date: "", cover_image_url: "", title: "", description: "",
  is_current: false, is_archived: false, article_ids: [],
});

export default function AdminIssues() {
  const [list, setList] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [i, a] = await Promise.all([api.get("/admin/issues"), api.get("/admin/articles")]);
    setList(i.data); setArticles(a.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (sel.id) await api.put(`/admin/issues/${sel.id}`, sel);
      else await api.post("/admin/issues", sel);
      toast.success("Issue saved");
      setSel(null); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (i) => {
    if (!window.confirm("Delete this issue?")) return;
    await api.delete(`/admin/issues/${i.id}`);
    load();
  };

  const uploadCover = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/uploads/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setSel({ ...sel, cover_image_url: data.stored_path });
  };

  return (
    <div data-testid="admin-issues">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-3xl text-[#001F3F]">Issues</h1>
        <button onClick={() => setSel(blank())} className="bg-[#001F3F] text-white px-5 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2"><Plus className="w-4 h-4" /> New Issue</button>
      </div>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mb-6"></div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {list.map((i) => (
          <div key={i.id} className="bg-white border border-gray-200 p-5">
            {i.cover_image_url && <img src={assetUrl(i.cover_image_url)} alt="" className="w-full h-40 object-cover border border-gray-100 mb-3" />}
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6]">Vol {i.volume} · Issue {i.issue} · {i.year}</div>
            <div className="font-serif text-lg text-[#001F3F] mt-1">{i.title || "Untitled"}</div>
            <div className="text-xs text-gray-500 mt-1">{(i.article_ids || []).length} article(s) · {i.is_current ? "Current" : "Archived"}</div>
            <div className="mt-3 flex gap-3 text-xs">
              <button onClick={() => setSel(i)} className="text-[#2BA6A6]">Edit</button>
              <button onClick={() => del(i)} className="text-red-500"><Trash2 className="w-3 h-3 inline" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {sel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-6">
          <div className="bg-white max-w-2xl w-full">
            <div className="p-5 bg-[#001F3F] text-white flex justify-between">
              <div className="font-serif text-xl">{sel.id ? "Edit Issue" : "New Issue"}</div>
              <button onClick={() => setSel(null)} className="text-white/80">✕</button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Volume" value={sel.volume} onChange={(v) => setSel({ ...sel, volume: v })} />
                <Field label="Issue" value={sel.issue} onChange={(v) => setSel({ ...sel, issue: v })} />
                <Field label="Year" value={sel.year} onChange={(v) => setSel({ ...sel, year: v })} />
              </div>
              <Field label="Title" value={sel.title} onChange={(v) => setSel({ ...sel, title: v })} />
              <Field label="Publication Date" value={sel.publication_date} onChange={(v) => setSel({ ...sel, publication_date: v })} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Description</label>
                <textarea rows={3} value={sel.description} onChange={(e) => setSel({ ...sel, description: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Cover Image</label>
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} />
                {sel.cover_image_url && <img src={assetUrl(sel.cover_image_url)} alt="" className="mt-2 max-h-40 border" />}
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Articles in this Issue</label>
                <div className="border border-gray-200 max-h-48 overflow-y-auto p-2">
                  {articles.length === 0 ? <div className="text-xs text-gray-500">No articles yet</div> : articles.map((a) => (
                    <label key={a.id} className="flex items-start gap-2 text-sm py-1">
                      <input type="checkbox" checked={(sel.article_ids || []).includes(a.id)} onChange={(e) => {
                        const ids = sel.article_ids || [];
                        setSel({ ...sel, article_ids: e.target.checked ? [...ids, a.id] : ids.filter((x) => x !== a.id) });
                      }} className="accent-[#2BA6A6] mt-1" />
                      <span className="text-xs">{a.title} <span className="text-gray-500">({a.article_type})</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sel.is_current} onChange={(e) => setSel({ ...sel, is_current: e.target.checked })} className="accent-[#2BA6A6]" /> Current Issue</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sel.is_archived} onChange={(e) => setSel({ ...sel, is_archived: e.target.checked })} className="accent-[#2BA6A6]" /> Archived</label>
              </div>
              <div className="pt-3">
                <button onClick={save} className="bg-[#001F3F] text-white px-5 py-2 text-xs uppercase tracking-wider hover:bg-[#2BA6A6]"><Save className="w-3 h-3 inline mr-1" /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 text-sm" />
    </div>
  );
}
